import React, { PropsWithChildren } from 'react';
import { useCreateBankOfEnglandBaseRate, useUpdateBankOfEnglandBaseRate } from '@app/sdk';
import { FormProvider, useForm } from 'react-hook-form';
import { ContentHeader, FormContainer, Input, InputProps } from '@app/shared';
import { RateModel } from '@app/models';
import { useRefreshBankOfEnglandBaseRates } from '../../../../sdk/tms/referenceData/UseBankOfEnglandBaseRates';

export function RateEditForm({ rate, children }: PropsWithChildren<{ rate: RateModel }>) {
  const formContext = useForm<RateModel>();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateBankOfEnglandBaseRate();
  const { mutateAsync: create, isPending: isCreating } = useCreateBankOfEnglandBaseRate();

  async function SaveRate(model: RateModel): Promise<string> {
    const request = { ...rate, ...model, rate: model.rate / 100 };

    if (request.id) {
      const updateResponse = await update({ rateId: request.id, request });
      return updateResponse.lastUpdatedDate;
    } else {
      const createResponse = await create(request);
      return createResponse.rateId;
    }
  }

  const refreshBankOfEnglandBaseRates = useRefreshBankOfEnglandBaseRates();
  const isSubmitting = () => isUpdating || isCreating;

  async function onSaveRate(model: RateModel) {
    await SaveRate(model);
    await refreshBankOfEnglandBaseRates();
  }

  const rateProps: InputProps<RateModel> = {
    name: 'rate',
    options: { required: 'Rate is required.' },
    defaultValue: rate?.rate,
    label: 'Rate',
    type: 'number',
    step: '.01',
    min: '0',
    max: '100',
    children: <div className="ui basic label">%</div>,
  };

  const announcementDateProps: InputProps<RateModel> = {
    name: 'announcementDate',
    options: { required: 'Announcement date is required' },
    defaultValue: rate?.announcementDate,
    label: 'Announcement Date',
    type: 'date',
  };

  const effectiveDateProps: InputProps<RateModel> = {
    name: 'effectiveDate',
    options: { required: 'Effective date is required' },
    defaultValue: rate?.effectiveDate,
    label: 'Effective Date',
    type: 'date',
  };

  return (
    <FormProvider {...formContext}>
      <ContentHeader>{rate ? 'Update Rate' : 'Add Rate'}</ContentHeader>
      <FormContainer onSubmit={onSaveRate} isSubmitting={isSubmitting()} disableButtons>
        <Input {...rateProps} disabled>
          <div className="ui basic label">%</div>
        </Input>
        <Input {...announcementDateProps} disabled />
        <Input {...effectiveDateProps} disabled />
        {children}
      </FormContainer>
    </FormProvider>
  );
}
